import { t } from "i18next";
import { match, P } from "ts-pattern";
import { z } from "zod/v4";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  FormSectionSchema,
  IncidentFormTypeSchema,
  longTextSchema,
  nonFutureDateTimeSchema,
  shortTextSchema,
  yesNoSchema,
  yesOnlySchema,
  type DetentionDetailsMinimumFields,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import {
  getFirstPlaceOfSafetyArrivalDateTime,
  hasPlaceOfSafetyInIncidentOrFormValues,
  isPoliceLeftTimeOutOfExpectedRange,
} from "../../workflowLogic/calculateDetentionTime.js";
import { getSupportedIncidentTypeSelection } from "../../workflowLogic/helpers/getIncidentTypeSelection.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";

export const formSpecification = new MultiPageFormBuilder()
  .withReadonlyFieldConditionallyVisible({
    fieldName: "endIncidentBanner",
    type: "banner",
    bannerType: "error",
    body: ({ incidentWorkItem }) => ({
      translationKey: tk("preEndIncident.warningBanner"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => false)
        .with(undefined, () => false)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "whyEndingMcaForm",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "startedFormInError" as const,
          "personHadCapacity" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("preEndIncident.whyEndingMcaForm.title"),
    requiredLabel: tk("preEndIncident.whyEndingMcaForm.required"),
    options: [
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.startedFormInError"),
        value: "startedFormInError",
      },
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.personHadCapacity"),
        value: "personHadCapacity",
      },
      {
        label: tk("preEndIncident.whyEndingMcaForm.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => true)
        .with("136", () => false)
        .with(undefined, () => false)
        .exhaustive(),
  })

  .withFieldConditionallyVisible({
    fieldName: "whyEndingMcaFormOther",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    label: tk("preEndIncident.whyEndingMcaFormOther.title"),
    multiline: true,
    rows: "5",
    requiredLabel: tk("preEndIncident.whyEndingMcaFormOther.required"),
    isVisible: ({ formValues }) =>
      formValues.whyEndingMcaForm?.value === "other",
  })

  .withFieldConditionallyVisible({
    fieldName: "whyNhsNotEngage",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "startedFormInError" as const,
          "internetConnectivityIssue" as const,
          "emailNotReceived" as const,
          "nhsUsingOtherProcess" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("preEndIncident.whyNhsNotEngage.title"),
    requiredLabel: tk("preEndIncident.whyNhsNotEngage.required"),
    options: [
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.startedFormInError"),
        value: "startedFormInError",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.internetConnection"),
        value: "internetConnectivityIssue",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.emailNotReceived"),
        value: "emailNotReceived",
      },
      {
        label: tk(
          "preEndIncident.whyNhsNotEngage.options.nhsUsingOtherProcess",
        ),
        value: "nhsUsingOtherProcess",
      },
      {
        label: tk("preEndIncident.whyNhsNotEngage.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ incidentWorkItem }) =>
      incidentWorkItem !== undefined &&
      match(
        getSupportedIncidentTypeSelection({ incidentWorkItem })
          // .orTee((e) => {
          //   console.error("Unsupported incident type", e.prettifyError());
          // })
          .unwrapOr(undefined),
      )
        .with("mca", () => false)
        .with("136", () => true)
        .with(undefined, () => true)
        .exhaustive(),
  })
  .withFieldConditionallyVisible({
    fieldName: "additionalInformationNotEngage",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.whyNhsNotEngage?.value === "other",
    label: tk("preEndIncident.whyNhsNotEngage.additionalInformationNotEngage"),
    requiredLabel: tk(
      "preEndIncident.whyNhsNotEngage.additionalInformationNotEngageRequired",
    ),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "dateTimeOfDetention",
    type: "date-time",
    timeLabel: tk("detentionDetails.timeOfDetention"),
    dateLabel: tk("detentionDetails.dateOfDetention"),
    requiredLabel: tk("detentionDetails.dateTimeOfDetentionRequired"),
    disableFuture: true,
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    isVisible: ({ formValues, incidentWorkItem }) => {
      if (formValues.whyNhsNotEngage?.value === "startedFormInError")
        return false;
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) => form.formType === IncidentFormTypeSchema.enum.section136,
          )
          ?.sections.find(
            (section) => section.id === FormSectionSchema.enum.detentionDetails,
          )?.data ?? (undefined as DetentionDetailsMinimumFields | undefined);

      const hasDateTimeDetentionStated = !!formSectionData?.dateTimeOfDetention;

      if (hasDateTimeDetentionStated) return false;

      return match(formValues)
        .with(
          {
            whyNhsNotEngage: {
              value: P.union(
                "emailNotReceived",
                "internetConnectivityIssue",
                "startedFormInError",
                "nhsUsingOtherProcess",
                "other",
              ),
            },
          },
          () => true,
        )
        .otherwise(() => false);
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "didNotArrive",
    type: "switch",
    ...new FieldBuilder()
      .withNullableSchema(yesOnlySchema)
      .captureValueLabel()
      .build(),
    label: tk("arrivePlaceOfSafety.didNotArrive"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      if (formValues.whyNhsNotEngage?.value === "startedFormInError")
        return false;

      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;

      if (hasArrivedPos) return false;

      return match(formValues.whyNhsNotEngage?.value)
        .with(
          "emailNotReceived",
          "internetConnectivityIssue",
          "nhsUsingOtherProcess",
          "other",
          () => true,
        )
        .with(undefined, () => false)
        .exhaustive();
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "arrivalDateTime",
    type: "date-time",
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("arrivePlaceOfSafety.arrivalDate"),
    timeLabel: tk("arrivePlaceOfSafety.arrivalTime"),
    disableFuture: true,
    label: tk("preEndIncident.didYouArriveAtPos"),
    requiredLabel: tk("arrivePlaceOfSafety.arrivalDateTimeRequired"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      if (formValues.whyNhsNotEngage?.value === "startedFormInError")
        return false;
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;

      if (hasArrivedPos || formValues.didNotArrive?.value === "yes")
        return false;

      return match(formValues.whyNhsNotEngage?.value)
        .with(
          "emailNotReceived",
          "internetConnectivityIssue",
          "nhsUsingOtherProcess",
          "other",
          () => true,
        )
        .with(undefined, () => false)
        .exhaustive();
    },
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "advisePhysicallyEnter",
    type: "banner",
    bannerType: "warning",
    isVisible: ({ formValues, incidentWorkItem }) => {
      if (formValues.whyNhsNotEngage?.value === "startedFormInError")
        return false;
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;

      // if arrivalDateTime wouldn’t be visible, don’t show this banner either
      if (hasArrivedPos || formValues.didNotArrive?.value === "yes")
        return false;

      const arrivalDateTimeVisible = match(formValues.whyNhsNotEngage?.value)
        .with(
          "emailNotReceived",
          "internetConnectivityIssue",
          "nhsUsingOtherProcess",
          "other",
          () => true,
        )
        .with(undefined, () => false)
        .exhaustive();

      if (!arrivalDateTimeVisible) return false;

      // now apply your original didNotArrive logic
      return match(formValues.didNotArrive)
        .with({ valueLabel: P._, value: "yes" }, () => false)
        .with(null, undefined, () => true)
        .otherwise(() => false);
    },
    body: tk("arrivePlaceOfSafety.advisePhysicallyEnter"),
  })

  .withFieldConditionallyVisible({
    fieldName: "nhsRequestedPolicePresentAtPos",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("preEndIncident.nhsRequestedPolicePresentAtPos"),
    requiredLabel: tk("preEndIncident.nhsRequestedPolicePresentAtPosRequired"),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
    isVisible: ({ formValues, incidentWorkItem }) => {
      if (formValues.whyNhsNotEngage?.value === "startedFormInError")
        return false;
      const formSectionData =
        incidentWorkItem?.forms
          .find(
            (form) =>
              form.formType === IncidentFormTypeSchema.enum.recordPlaceOfSafety,
          )
          ?.sections.find(
            (section) =>
              section.id === FormSectionSchema.enum.recordPlaceOfSafetyArrival,
          )?.data ?? undefined;

      const hasArrivedPos = !!formSectionData?.arrivalDateTime;
      const hasArrivedManualOverride =
        hasArrivedPos || !!formValues.arrivalDateTime?.value;

      if (!hasArrivedManualOverride) return false;

      return match(formValues)
        .with(
          {
            whyNhsNotEngage: {
              value: P.union(
                "emailNotReceived",
                "internetConnectivityIssue",
                "startedFormInError",
                "nhsUsingOtherProcess",
                "other",
              ),
            },
          },
          () => true,
        )
        .otherwise(() => false);
    },
  })

  .withFieldConditionallyVisible({
    fieldName: "whoRequestedPolicePresence",
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.nhsRequestedPolicePresentAtPos?.value === "yes",
    label: tk("preEndIncident.whoRequestedPolicePresence"),
    requiredLabel: tk("preEndIncident.whoRequestedPolicePresenceReason"),
    multiline: true,
  })

  .withFieldConditionallyVisible({
    fieldName: "reasonForPolicePresenceRequest",
    type: "dropdown",
    isVisible: ({ formValues }) =>
      formValues.nhsRequestedPolicePresentAtPos?.value === "yes",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "notEnoughStaff" as const,
          "immediateRiskToLifeOrOfSeriousHarm" as const,
          "transferred" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("preEndIncident.reasonForPolicePresenceRequest.title"),
    requiredLabel: tk("preEndIncident.reasonForPolicePresenceRequest.required"),
    options: ({ incidentWorkItem }) =>
      [
        {
          label: tk(
            "preEndIncident.reasonForPolicePresenceRequest.options.notEnoughStaff",
          ),
          value: "notEnoughStaff",
        },
        {
          label: tk(
            "preEndIncident.reasonForPolicePresenceRequest.options.immediateRiskToLifeOrOfSeriousHarm",
          ),
          value: "immediateRiskToLifeOrOfSeriousHarm",
        },
        {
          label: tk(
            "preEndIncident.reasonForPolicePresenceRequest.options.transferred",
          ),
          value: "transferred",
        },
        {
          label: tk(
            "preEndIncident.reasonForPolicePresenceRequest.options.other",
          ),
          value: "other",
        },
      ].map((option) => ({
        label: {
          translationKey: option.label,
          translationOptions: {
            context: getWorkflowTranslationContext({ incidentWorkItem }),
          },
        },
        value: option.value,
      })),
  })

  .withFieldConditionallyVisible({
    fieldName: "reasonForPolicePresenceRequestAdditionalInformation",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.nhsRequestedPolicePresentAtPos?.value === "yes",
    label: tk(
      "preEndIncident.reasonForPolicePresenceRequest.additionalInformation",
    ),
    requiredLabel: tk(
      "preEndIncident.reasonForPolicePresenceRequest.additionalInformationRequired",
    ),
    multiline: true,
    rows: "5",
  })

  .withReadonlyFieldConditionallyVisible({
    fieldName: "ifDisagreeBanner",
    type: "banner",
    bannerType: "warning",
    isVisible: ({ formValues }) =>
      formValues.nhsRequestedPolicePresentAtPos?.value === "yes",
    body: tk("preEndIncident.reasonForPolicePresenceRequest.ifDisagreeBanner"),
  })
  .withFieldConditionallyVisible({
    fieldName: "whatWillYouDo",
    type: "radio",
    isVisible: ({ formValues }) =>
      formValues.nhsRequestedPolicePresentAtPos?.value === "yes",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "escalatedAndLeft" as const,
          "stayedAgreeWithNhs" as const,
          "stayedDisagreeWithNhs" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    label: tk("preEndIncident.whatWillYouDo.title"),
    requiredLabel: tk("preEndIncident.whatWillYouDo.required"),
    options: [
      {
        label: tk("preEndIncident.whatWillYouDo.options.stayedAgreeWithNhs"),
        value: "stayedAgreeWithNhs",
      },
      {
        label: tk("preEndIncident.whatWillYouDo.options.stayedDisagreeWithNhs"),
        value: "stayedDisagreeWithNhs",
      },
      {
        label: tk("preEndIncident.whatWillYouDo.options.escalatedAndLeft"),
        value: "escalatedAndLeft",
      },
    ],
  })

  .withFieldConditionallyVisible({
    fieldName: "additionalInformation",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.nhsRequestedPolicePresentAtPos?.value === "yes",
    label: tk("preEndIncident.whatWillYouDo.additionalInformation"),
    multiline: true,
    rows: "5",
  })

  .withFieldConditionallyVisible({
    fieldName: "remainedAtPos",
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("preEndIncident.remainedAtPos"),
    requiredLabel: tk("preEndIncident.remainedAtPosRequired"),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
    isVisible: ({ formValues }) => {
      return formValues.nhsRequestedPolicePresentAtPos?.value === "no";
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "remainedAtPosReasoning",
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.nhsRequestedPolicePresentAtPos?.value === "no",
    label: tk("preEndIncident.remainedAtPosReasoning"),
    requiredLabel: tk("preEndIncident.remainedAtPosReasoningRequired"),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "leavePos",
    type: "date-time",
    ...new FieldBuilder().withSchema(nonFutureDateTimeSchema).build(),
    dateLabel: tk("endIncidentCommon.incidentLeaveDatePos"),
    timeLabel: tk("endIncidentCommon.incidentLeaveTimePos"),
    requiredLabel: tk("endIncidentCommon.incidentLeaveDateTimePosRequired"),
    disableFuture: true,
    isVisible: ({ formValues }) =>
      formValues.whyNhsNotEngage?.value !== "startedFormInError" &&
      (formValues.didNotArrive?.value === "yes" ||
        !!formValues.nhsRequestedPolicePresentAtPos?.value),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "incidentDuration",
    type: "incident-duration",
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("finishForm.totalPosTime"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    getDateTimeLeft: ({ formValues }) => formValues.leavePos?.value,
    getArrivalAtFirstPlaceOfSafetyDateTime: ({
      formValues,
      incidentWorkItem,
    }) =>
      getFirstPlaceOfSafetyArrivalDateTime(incidentWorkItem) ??
      formValues.arrivalDateTime?.value,
    isVisible: ({ formValues, incidentWorkItem }) => {
      const hasPlaceOfSafety =
        hasPlaceOfSafetyInIncidentOrFormValues(incidentWorkItem);

      if (formValues.didNotArrive?.value === "yes" && !hasPlaceOfSafety) {
        return false;
      }

      return (
        formValues.whyNhsNotEngage?.value !== "startedFormInError" &&
        !!formValues.nhsRequestedPolicePresentAtPos?.value
      );
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmDateTimeLeaving",
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    label: tk("finishForm.confirmDateTimeLeaving"),
    requiredLabel: tk("finishForm.confirmDateTimeLeavingRequired"),
    isVisible: ({ formValues, incidentWorkItem }) => {
      if (formValues.leavePos === undefined || formValues.leavePos === null) {
        return false;
      }
      const hasPlaceOfSafety =
        hasPlaceOfSafetyInIncidentOrFormValues(incidentWorkItem);

      if (formValues.didNotArrive?.value === "yes" && !hasPlaceOfSafety) {
        return false;
      }
      if (
        formValues.whyNhsNotEngage?.value !== "startedFormInError" &&
        !!formValues.nhsRequestedPolicePresentAtPos?.value
      ) {
        return isPoliceLeftTimeOutOfExpectedRange(
          formValues.leavePos.value,
          incidentWorkItem,
          formValues.arrivalDateTime?.value,
        );
      }
      return false;
    },
  })

  .withReadonlyFieldConditionallyVisible({
    fieldName: "informStaffBeforeLeavingBanner",
    type: "banner",
    bannerType: "info",
    isVisible: ({ formValues }) =>
      formValues.whyNhsNotEngage?.value !== "startedFormInError" &&
      (formValues.didNotArrive?.value === "yes" ||
        !!formValues.nhsRequestedPolicePresentAtPos?.value),
    body: tk("preEndIncident.informStaffBeforeLeaving"),
  })

  .withFieldConditionallyVisible({
    fieldName: "selectedAssessmentOutcome",
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "mentalExamination",
          "signpostedToCommunity",
          "informalAdmission",
          "section2Detention",
          "section3Detention",
          "unknownHandover",
          "unknownMaximumDetention",
        ]),
      )
      .captureValueLabel()
      .build(),

    isVisible: ({ formValues, incidentWorkItem }) => {
      return (
        incidentWorkItem !== undefined &&
        match(
          getSupportedIncidentTypeSelection({ incidentWorkItem })
            // .orTee((e) => {
            //   console.error("Unsupported incident type", e.prettifyError());
            // })
            .unwrapOr(undefined),
        )
          .with("mca", () => false)
          .with("136", () => true)
          .with(undefined, () => true)
          .exhaustive() &&
        formValues.whyNhsNotEngage?.value !== "startedFormInError"
      );
    },
    label: tk("endIncidentCommon.incidentOutcome.title"),
    requiredLabel: tk("endIncidentCommon.incidentOutcome.required"),
    options: [
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.mentalExamination",
        ),
        value: "mentalExamination",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.signpostedToCommunity",
        ),
        value: "signpostedToCommunity",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.informalAdmission",
        ),
        value: "informalAdmission",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.section2Detention",
        ),
        value: "section2Detention",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.section3Detention",
        ),
        value: "section3Detention",
      },
      {
        label: tk("endIncidentCommon.incidentOutcome.options.unknownHandover"),
        value: "unknownHandover",
      },
      {
        label: tk(
          "endIncidentCommon.incidentOutcome.options.unknownMaximumDetention",
        ),
        value: "unknownMaximumDetention",
      },
    ],
  })
  .withPage([
    // We need to ensure that there is something visible on page 1
    "endIncidentBanner",
    "whyNhsNotEngage",
    "additionalInformationNotEngage",
  ])
  .withPage(["whyEndingMcaForm", "whyEndingMcaFormOther"])
  .withPage(["dateTimeOfDetention"])
  .withPage(["didNotArrive", "arrivalDateTime", "advisePhysicallyEnter"])
  .withPage(["nhsRequestedPolicePresentAtPos"])
  .withPage(["whoRequestedPolicePresence"])
  .withPage([
    "reasonForPolicePresenceRequest",
    "reasonForPolicePresenceRequestAdditionalInformation",
    "ifDisagreeBanner",
  ])
  .withPage(["whatWillYouDo", "additionalInformation"])
  .withPage(["remainedAtPos", "remainedAtPosReasoning"])
  .withPage([
    "leavePos",
    "incidentDuration",
    "confirmDateTimeLeaving",
    "informStaffBeforeLeavingBanner",
  ])
  .withPage(["selectedAssessmentOutcome"])
  .build({ t });

export type PreEndIncidentFormData = ReturnType<
  typeof formSpecification
>["formType"];
